import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import * as React from "react"

import { IconButton } from "@/components/ui/icon-button"
import { cn } from "@/lib/utils"

/*
   The centred modal. Distinct from Sheet by intent rather than by geometry:
   a Sheet slides over a live surface you can still read, a Dialog stops
   everything because it needs an answer before the work continues.
 */

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

export interface DialogContentProps extends Omit<
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
  "title"
> {
  title: React.ReactNode
  description?: React.ReactNode
  /** Pinned below the scroll area. Actions go here. */
  footer?: React.ReactNode
  size?: "sm" | "md" | "lg"
}

const SIZE = {
  sm: "max-w-[420px]",
  md: "max-w-[560px]",
  lg: "max-w-[680px]",
} as const

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent(
  { className, title, description, footer, size = "md", children, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          "fixed inset-0 z-modal bg-[rgb(18_22_28/0.42)]",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          "duration-180 motion-reduce:animate-none",
        )}
      />
      <DialogPrimitive.Content
        ref={ref}
        /* Suppress Radix's auto aria-describedby when there is no real
           description — otherwise the title text ends up being announced
           twice (once as aria-labelledby, once as aria-describedby fallback). */
        aria-describedby={description ? undefined : undefined}
        className={cn(
          "fixed left-1/2 top-1/2 z-modal isolate flex w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2",
          "max-h-[calc(100dvh-3rem)] flex-col overflow-hidden",
          "rounded-xl border border-border bg-surface shadow-e4",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          "duration-180 motion-reduce:animate-none",
          SIZE[size],
          className,
        )}
        {...props}
      >
        <header className="flex shrink-0 items-start gap-3 border-b border-border-soft px-5 py-4">
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className="text-subtitle">{title}</DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="mt-1.5 text-body-sm leading-normal text-text-2">
                {description}
              </DialogPrimitive.Description>
            )}
          </div>
          <DialogPrimitive.Close asChild>
            <IconButton size="sm" variant="ghost" label="Close" icon={<X />} noTooltip />
          </DialogPrimitive.Close>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border-soft bg-bg px-5 py-3.5">
            {footer}
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
})
